import { createSignal, type Accessor, type Setter } from 'solid-js'
import { createStore, produce } from 'solid-js/store'
import type { IObjectImpl, ImplRegistry } from './interactions/ObjectImpl'
import type { Position } from '../types'

// Recorded when selection is transferred via an intercept-rect redirect so the
// newly-selected element can detect a subsequent quick click as a double-click.
export interface SelectGesture {
	id:   string
	time: number
	pos:  Position
}

export interface SelectionContext {
	selected: Accessor<string | null>
	selectedIds: Accessor<ReadonlySet<string>>
	select: (id: string) => void
	selectAt: (id: string, pos: Position) => void
	toggleSelect: (id: string) => void
	deselect: () => void

	// The pinned object, or null. Pinning brings up an object's intercept rect at once instead of on the
	// double-click delay a click-selected object waits out (see interceptReady). It is for the selections
	// made *without* a mousedown landing on the object itself — Alt-click cycling, and the container that
	// a click on one of its children selects — where the object has no timer of its own to arm and the
	// next plain click would otherwise land on whatever covers it and select that instead.
	// Cleared by any other selection change.
	pinned: Accessor<string | null>
	selectPinned: (id: string) => void
	editing: Accessor<string | null>
	startEdit: (id: string) => void
	stopEdit: () => void
	shiftHeld: Accessor<boolean>
	setShiftHeld: Setter<boolean>
	selectGesture: Accessor<SelectGesture | null>
	clearSelectGesture: () => void

	// The live registry of mounted object impls (id → impl). Each SelectableObject registers its impl
	// here on setup and unregisters it on cleanup; geometry/command code reads live bboxes and drives
	// writePos/refreshSize through it. Kept here so selectedImpl() can bridge the current
	// selection to its impl without a second context.
	impls: ImplRegistry
	// Register/unregister an impl — the only way to mutate the registry (the raw store setter is private).
	// Unregister is deferred (see createSelectionState) so a cleanup-time removal never mutates the
	// reactive registry mid-disposal.
	registerImpl: (id: string, impl: IObjectImpl) => void
	unregisterImpl: (id: string, impl: IObjectImpl) => void
	// The impl of the currently-selected object, or null when nothing (or an unmounted object) is selected.
	selectedImpl: () => IObjectImpl | null
	// Re-fits the selected text object's box after a style change (font size etc.); no-op otherwise.
	refreshSelectedSize: () => void
}

export function createSelectionState(): SelectionContext {
	const [selected,       setSelected]       = createSignal<string | null>(null)
	const [selectedIds,    setSelectedIds]    = createSignal<ReadonlySet<string>>(new Set())
	const [editing,        setEditing]        = createSignal<string | null>(null)
	const [shiftHeld,      setShiftHeld]      = createSignal(false)
	const [selectGesture,  setSelectGesture]  = createSignal<SelectGesture | null>(null)
	const [pinned,         setPinned]         = createSignal<string | null>(null)
	const [impls,          setImpls]          = createStore<ImplRegistry>({})

	function registerImpl(id: string, impl: IObjectImpl) {
		setImpls(id, impl)
	}

	// Deferred to a microtask: unregister runs from a SelectableObject onCleanup, i.e. synchronously
	// inside Solid's cleanNode. Writing the reactive registry there re-runs subscribers mid-disposal and
	// corrupts the reactive graph (cleanNode later dereferences a nulled slot → page-leave crash). The
	// identity check skips the delete if the id was re-registered meanwhile (undo/redo of a delete).
	function unregisterImpl(id: string, impl: IObjectImpl) {
		queueMicrotask(() => {
			if (impls[id] === impl)
				setImpls(produce(s => { delete s[id] }))
		})
	}

	function selectedImpl(): IObjectImpl | null {
		const id = selected()
		return id ? (impls[id] ?? null) : null
	}

	function refreshSelectedSize() {
		const id = selected()
		if (id) impls[id]?.refreshSize?.()
	}

	function stopEdit() { setEditing(null) }
	function startEdit(id: string) { setEditing(id) }
	function clearSelectGesture() { setSelectGesture(null) }

	function select(id: string) {
		if (editing() && editing() !== id) stopEdit()
		setSelected(id)
		setSelectedIds(new Set([id]))
		setSelectGesture(null)   // clear any pending gesture on a plain select
		setPinned(null)
	}

	// Select and pin (see `pinned`). Pins after select() so it survives the clear select() does.
	function selectPinned(id: string) {
		select(id)
		setPinned(id)
	}

	// Like select(), but also records the click position so the newly-selected
	// element can detect a follow-up quick click as a manual double-click even
	// when its own onMouseDown never saw the first click (e.g. it was intercepted
	// by the previously-selected element's intercept rect).
	function selectAt(id: string, pos: Position) {
		select(id)
		setSelectGesture({ id, time: Date.now(), pos })
	}

	function toggleSelect(id: string) {
		if (editing()) stopEdit()
		setSelectGesture(null)
		setPinned(null)
		const prev = selectedIds()
		const next = new Set(prev)
		if (next.has(id)) {
			next.delete(id)
			if (selected() === id) {
				const remaining = [...next]
				setSelected(remaining.length > 0 ? remaining[remaining.length - 1] : null)
			}
		} else {
			next.add(id)
			setSelected(id)
		}
		setSelectedIds(next)
	}

	function deselect() {
		stopEdit()
		setSelectGesture(null)
		setSelected(null)
		setSelectedIds(new Set<string>())
		setPinned(null)
	}

	return { selected, selectedIds, select, selectAt, toggleSelect, deselect, pinned, selectPinned, editing, startEdit, stopEdit, shiftHeld, setShiftHeld, selectGesture, clearSelectGesture, impls, registerImpl, unregisterImpl, selectedImpl, refreshSelectedSize }
}
